import { useEffect, useRef, useState } from "react";

const pilares = [
  {
    id: "intelligence",
    name: "Ferrarezi Intelligence",
    tagline: "Painel executivo de saúde e riscos",
    desc: "Visibilidade total sobre os indicadores de saúde da sua empresa. Dashboards executivos com dados em tempo real para decisões estratégicas baseadas em evidências.",
    icon: "ri-brain-line",
    color: "#FBC52D",
    metrics: [
      { label: "Sinistralidade", value: "12%" },
      { label: "Absenteísmo", value: "-34%" },
      { label: "ROI Saúde", value: "+2.8x" },
    ],
    img: "https://readdy.ai/api/search-image?query=executive%20dashboard%20data%20analytics%20screens%20glowing%20charts%20graphs%20in%20dark%20modern%20office%2C%20business%20intelligence%20technology%2C%20golden%20amber%20light%20on%20dark%20background%2C%20premium%20corporate%20data%20visualization%2C%20futuristic%20control%20room&width=600&height=400&seq=eco_intel001&orientation=landscape",
  },
  {
    id: "care",
    name: "Ferrarezi Care",
    tagline: "Concierge premium 24h",
    desc: "Atendimento hiperpersonalizado para colaboradores e gestores. Suporte humano combinado com inteligência artificial disponível 24 horas por dia, 7 dias por semana.",
    icon: "ri-customer-service-2-line",
    color: "#FBC52D",
    metrics: [
      { label: "Disponibilidade", value: "24/7" },
      { label: "Satisfação", value: "98%" },
      { label: "Tempo resposta", value: "<2min" },
    ],
    img: "https://readdy.ai/api/search-image?query=premium%20concierge%20service%20professional%20customer%20care%20team%20in%20modern%20office%2C%20warm%20golden%20light%2C%20sophisticated%20corporate%20environment%2C%20high-end%20service%20concept%2C%20elegant%20minimal%20workspace%2C%20people%20helping%20people&width=600&height=400&seq=eco_care001&orientation=landscape",
  },
  {
    id: "analytics",
    name: "Ferrarezi Analytics",
    tagline: "Dados, sinistralidade e absenteísmo",
    desc: "Controle total sobre os dados de saúde corporativa. Análise preditiva de sinistralidade, mapeamento de absenteísmo e relatórios estratégicos para o RH.",
    icon: "ri-line-chart-line",
    color: "#FBC52D",
    metrics: [
      { label: "Relatórios", value: "Mensal" },
      { label: "Precisão", value: "99.2%" },
      { label: "Indicadores", value: "+40" },
    ],
    img: "https://readdy.ai/api/search-image?query=data%20analytics%20business%20intelligence%20charts%20graphs%20on%20multiple%20screens%2C%20modern%20tech%20office%20dark%20environment%2C%20glowing%20data%20visualization%2C%20corporate%20analytics%20dashboard%2C%20golden%20light%20accents%2C%20futuristic%20data%20center&width=600&height=400&seq=eco_analytics001&orientation=landscape",
  },
  {
    id: "human",
    name: "Ferrarezi Human",
    tagline: "Saúde emocional e cultura organizacional",
    desc: "Humanização do RH com foco em saúde emocional, bem-estar e cultura. Programas estruturados para engajamento, prevenção de burnout e fortalecimento da identidade organizacional.",
    icon: "ri-mental-health-line",
    color: "#FBC52D",
    metrics: [
      { label: "Engajamento", value: "+67%" },
      { label: "Turnover", value: "-28%" },
      { label: "Bem-estar", value: "9.4/10" },
    ],
    img: "https://readdy.ai/api/search-image?query=diverse%20team%20of%20professionals%20in%20collaborative%20modern%20workspace%2C%20emotional%20wellbeing%20corporate%20culture%2C%20warm%20golden%20natural%20light%2C%20people%20connecting%20and%20collaborating%2C%20premium%20office%20environment%2C%20human%20centered%20design&width=600&height=400&seq=eco_human001&orientation=landscape",
  },
  {
    id: "integra",
    name: "Ferrarezi Integra",
    tagline: "Ecossistema completo de benefícios",
    desc: "Gestão integrada de todos os benefícios corporativos em uma única plataforma. Conectamos seguros, saúde, previdência e benefícios flexíveis em um ecossistema coeso e eficiente.",
    icon: "ri-links-line",
    color: "#FBC52D",
    metrics: [
      { label: "Produtos", value: "80+" },
      { label: "Integrações", value: "100%" },
      { label: "Economia", value: "até 35%" },
    ],
    img: "https://readdy.ai/api/search-image?query=integrated%20technology%20ecosystem%20connected%20platforms%20digital%20network%2C%20modern%20corporate%20tech%20infrastructure%2C%20golden%20light%20on%20dark%20background%2C%20premium%20business%20technology%20concept%2C%20interconnected%20systems%20visualization&width=600&height=400&seq=eco_integra001&orientation=landscape",
  },
];

export default function Ecossistema() {
  const [active, setActive] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".eco-item").forEach((item, i) => {
              setTimeout(() => item.classList.add("eco-visible"), i * 80);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const current = pilares[active];

  return (
    <section id="ecossistema" className="py-16 lg:py-20 bg-[#0D0D0D] overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 eco-item opacity-0 translate-y-6 transition-all duration-500">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="w-6 h-px bg-[#FBC52D]" />
            <span className="text-[#FBC52D] text-xs font-semibold tracking-widest uppercase">
              Nossos Pilares
            </span>
            <span className="w-6 h-px bg-[#FBC52D]" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
            O Ecossistema Ferrarezi
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed">
            Cinco pilares integrados que transformam a gestão de pessoas em uma vantagem competitiva real para sua empresa.
          </p>
        </div>

        {/* Pillar Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12 eco-item opacity-0 translate-y-6 transition-all duration-500">
          {pilares.map((pilar, idx) => (
            <button
              key={pilar.id}
              onClick={() => setActive(idx)}
              className={`whitespace-nowrap flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer border ${
                active === idx
                  ? "bg-[#FBC52D] text-black border-[#FBC52D] shadow-lg shadow-[#FBC52D]/20"
                  : "bg-white/10 text-white/90 border-white/15 hover:bg-white/15 hover:text-white hover:border-[#FBC52D]/30"
              }`}
            >
              <i className={`${pilar.icon} text-base`} />
              <span>{pilar.name.replace("Ferrarezi ", "")}</span>
            </button>
          ))}
        </div>

        {/* Active Pilar Detail */}
        <div className="eco-item opacity-0 translate-y-6 transition-all duration-500">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Left: Info */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 lg:p-10 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#FBC52D]/15 border border-[#FBC52D]/30">
                    <i className={`${current.icon} text-[#FBC52D] text-2xl`} />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-xl">{current.name}</h3>
                    <p className="text-[#FBC52D] text-sm font-medium">{current.tagline}</p>
                  </div>
                </div>

                <p className="text-gray-400 text-base leading-relaxed mb-8">
                  {current.desc}
                </p>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                {current.metrics.map((m) => (
                  <div key={m.label} className="bg-white/5 rounded-xl p-2 sm:p-3 lg:p-4 text-center border border-white/5">
                    <div className="text-[#FBC52D] text-sm sm:text-lg lg:text-xl font-bold mb-0.5 sm:mb-1">{m.value}</div>
                    <div className="text-white/50 text-[10px] sm:text-xs">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Image */}
            <div className="relative rounded-2xl overflow-hidden h-72 lg:h-auto min-h-64">
              <img
                key={current.id}
                src={current.img}
                alt={current.name}
                className="w-full h-full object-cover object-top transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" />

              {/* Dashboard overlay */}
              <div className="absolute top-5 right-5 bg-black/70 backdrop-blur-md border border-[#FBC52D]/20 rounded-xl p-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#FBC52D] animate-pulse" />
                  <span className="text-white text-xs font-semibold">Live Dashboard</span>
                </div>
              </div>

              <div className="absolute bottom-5 left-5">
                <span className="bg-[#FBC52D] text-black text-xs font-bold px-3 py-1.5 rounded-full">
                  {current.name}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="eco-item opacity-0 translate-y-6 transition-all duration-500 mt-12 bg-[#FBC52D]/10 border border-[#FBC52D]/25 rounded-2xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-white font-bold text-xl mb-2">Quer ver o ecossistema na prática?</h3>
            <p className="text-gray-400 text-sm">Agende uma demo gratuita e veja como cada pilar pode transformar o seu RH.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button
              onClick={() => document.querySelector("#contato")?.scrollIntoView({ behavior: "smooth" })}
              className="whitespace-nowrap bg-[#FBC52D] text-black font-semibold px-7 py-3 rounded-full hover:bg-[#d4a800] transition-colors text-sm cursor-pointer"
            >
              Agendar demo gratuita
            </button>
            <a
              href="https://wa.me/5511936189998"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 border border-white/20 text-white font-semibold px-7 py-3 rounded-full hover:bg-white/10 transition-colors text-sm cursor-pointer"
            >
              <i className="ri-whatsapp-line" />
              WhatsApp
            </a>
          </div>
        </div>

        {/* Bottom mini cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-8">
          {pilares.map((pilar, idx) => (
            <button
              key={pilar.id}
              onClick={() => setActive(idx)}
              className={`eco-item opacity-0 translate-y-6 transition-all duration-500 p-4 rounded-xl border text-left cursor-pointer group ${
                active === idx
                  ? "bg-[#FBC52D]/10 border-[#FBC52D]/40"
                  : "bg-white/3 border-white/8 hover:bg-white/8 hover:border-white/20"
              }`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg mb-3 ${active === idx ? "bg-[#FBC52D]/20" : "bg-white/5"}`}>
                <i className={`${pilar.icon} text-sm ${active === idx ? "text-[#FBC52D]" : "text-white/50"}`} />
              </div>
              <p className={`text-xs font-semibold leading-tight ${active === idx ? "text-[#FBC52D]" : "text-white/50"}`}>
                {pilar.name.replace("Ferrarezi ", "")}
              </p>
            </button>
          ))}
        </div>
      </div>

      <style>{`
        .eco-visible {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </section>
  );
}